<template>
  <BottomDrawer
    v-model="visible"
    :draggable="true"
    :close-threshold="100"
    :close-on-click-mask="true"
    :lock-scroll="true"
    class="m-only"
    @close="handleClose"
  >
    <div v-if="userInfo" class="user-pop__content">
      <!-- 账号区域 -->
      <div class="user-pop__account">
        <p class="user-pop__label">账号</p>
        <div class="user-pop__user-row">
          <div class="user-pop__user-info">
            <Resource
              :src="userInfo?.logo_url"
              :transform-resource="transformImage"
              class="user-pop__avatar"
              alt="avatar"
            />
            <p class="user-pop__user-name">
              {{ userInfo?.account_name }}
              <!-- 个人账号不显示组织名称 -->
              <template v-if="showOrgName && userInfo?.organization_name"
                >（{{ userInfo.organization_name }}）</template
              >
            </p>
          </div>
          <RightTag
            v-if="userInfo?.rights_type"
            class="user-pop__level-tag"
            :rights-type="userInfo.rights_type"
          />
        </div>
        <div class="user-pop__divider"></div>
      </div>

      <!-- 列表区域 -->
      <div class="user-pop__list">
        <div
          v-if="!isInCampaign?.flag"
          class="user-pop__list-item user-pop__list-item--invite"
          @click="handleInvite"
        >
          <span class="user-pop__list-text">推荐给好友</span>
          <div class="user-pop__invite-tag">
            <IconGift class="user-pop__gift-icon" />
            <span>邀请有礼</span>
          </div>
        </div>
        <div v-else class="user-pop__list-item user-pop__list-item--invite" @click="handleNewYear">
          <span class="user-pop__list-text">推荐给好友</span>
          <div class="user-pop__invite-tag">
            <IconGift class="user-pop__gift-icon" />
            <span>新春活动</span>
          </div>
        </div>
        <div class="user-pop__list-item user-pop__list-item--logout" @click="handleLogout">
          <span class="user-pop__list-text">退出登录</span>
        </div>
      </div>

      <!-- 按钮区域 -->
      <div class="user-pop__btn-section">
        <div class="user-pop__btn-group">
          <button class="user-pop__btn user-pop__btn--outline" @click="handleConsult">
            企业咨询
          </button>
          <button class="user-pop__btn user-pop__btn--primary" @click="handleGoPC">
            前往电脑端 体验完整功能
          </button>
        </div>
        <p class="user-pop__hint">点击复制有言官网链接，或使用电脑浏览器搜索"有言"登录</p>
      </div>
    </div>
  </BottomDrawer>
</template>

<script lang="ts" setup>
import RightTag from '@rendora/common/features/right-tag/index.vue';
import { IRightsType } from '@rendora/common/types/enums';
import BottomDrawer from '@rendora/design/components/bottom-drawer/BottomDrawer.vue';
import { message } from 'ant-design-vue';
import Resource from '~/components/resource';
import { useLoginOut } from '~/composables/user';

import { PositionCode } from '@rendora/common/entities/analysis_backend';
import { getRightsIsInCampaign, type IRightsIsInCampaign } from '@rendora/common/entities/rights';
import { useContactUs } from '@rendora/common/features/contact/libs/useContactUs';
import IconGift from '~/design/icons/gift.svg?component';
import { transformImage } from '~/utils';
import { copy } from '~/utils/copy';

const userInfo = useUserInfo();

const visible = ref(false);
const isInCampaign = ref<IRightsIsInCampaign | undefined>();

const showOrgName = computed(() => {
  return ![IRightsType.PERSONAL, IRightsType.PERSONAL_LITE, IRightsType.TASTE].includes(
    userInfo.value?.rights_type ?? IRightsType.TASTE,
  );
});

const fetchNotice = async () => {
  try {
    const res = await getRightsIsInCampaign();
    isInCampaign.value = res.data;
  } catch (err) {
    console.error(err);
  }
};

const open = () => {
  visible.value = true;
};

const close = () => {
  visible.value = false;
};

const handleClose = () => {
  visible.value = false;
};

const handleInvite = () => {
  // 推荐给好友逻辑
  navigateTo('/pull-new');
  close();
};

const handleNewYear = () => {
  // 推荐给好友逻辑
  navigateTo('/new-year');
  close();
};

const handleLogout = async () => {
  close();
  await useLoginOut();
};

const handleConsult = () => {
  useContactUs(PositionCode['有言官网-企业咨询'], '企业需求咨询', '扫码加行业专家·快速响应');
  close();
};

const handleGoPC = async () => {
  const url = window.location.origin;
  try {
    await copy(url);
    message.success('成功复制链接，请使用电脑登录');
  } catch (_e) {
    message.info('链接获取失败，请使用电脑浏览器搜索“有言”登录使用');
  }
  close();
};

onMounted(() => {
  fetchNotice();
});

defineExpose({
  open,
  close,
});
</script>

<style lang="scss" scoped src="./mobile.scss"></style>
